import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Drawer from "react-modern-drawer";
import { toast } from "react-toastify";
import { get } from "../../../../../utils/helpers/api";
import DOC_STYLES from "../../../utils/docStyles";
import { IoClose, IoCreateOutline, IoPrintOutline, IoShareSocialOutline } from "react-icons/io5";
import { BsTrash3 } from 'react-icons/bs';
import DefaultButton from "../../../../../components/Buttons/Default";
import { Spinner } from "reactstrap";
import { PRIMARY } from "../../../../../utils/styles/colors";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../../context/settings";
import DefaultDrawer from "../../../../../components/Drawers/Default";
import { printSlug } from "../../../../../utils/helpers/text";
import { AWS_S3_URL } from "../../../../../utils/constants";

export default function ViewDrawer({paymentId, isOpen, onClose, onDelete, refreshPayments}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useSettings();
  const [payment, setPayment] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);

  const getPayment = async () => {
    if (!paymentId) return;
    
    try {
      const data = await get(`/sales/payments/view/${paymentId}`);
      setPayment(data.payment ?? null);
      setCompanyData(data.company ?? null);
    } catch (error) {
      toast.error(t('sales.failedLoadInvoice'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!paymentId) return;
    setLoading(true);
    setPayment(null);
    getPayment();
  }, [paymentId]);

  return(
    <DefaultDrawer 
      isOpen={isOpen} 
      size={680} 
      toggle={onClose}
    >
      <div style={DOC_STYLES.wrap}>
        {/* ── Header ── */}
        <div style={DOC_STYLES.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}
              onClick={onClose}
              onMouseEnter={e => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.color = "#333"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#888"; }}
            >
              <IoClose size={20} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1d1d1f" }}>{t('sales.paymentDetails')}</span>
          </div>
        </div>

        {/* ── Action Bar ── */}
        <div style={DOC_STYLES.actionBar}>
          <DefaultButton className="btn-action" onClick={() => navigate(`/sales/payments/edit/${paymentId}`, { state: { paymentData: payment } })} size="sm" disabled={!payment} color="light" outline>
            <IoCreateOutline size={15} />
            {t('common.edit')}
          </DefaultButton>
          <DefaultButton className="btn-action" onClick={() => window.print()} size="sm" disabled={!payment} color="light" outline>
            <IoPrintOutline size={15} />
            {t('common.print')}
          </DefaultButton>
          <DefaultButton className="btn-action" onClick={onDelete} size="sm" disabled={!payment} color="light" outline>
            <BsTrash3 size={15} />
            {t('common.delete')}
          </DefaultButton>
        </div>

        {/* ── Scrollable document area ── */}
        <div style={DOC_STYLES.scroll}>
          {loading && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
              <Spinner color="primary" />
            </div>
          )}

          {!loading && payment && (
            <div id="quote-print-area" style={DOC_STYLES.paper}>

              {/* Company + Title */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36 }}>
                <div>
                  <img
                    src={companyData?.logo_path ? AWS_S3_URL + companyData.logo_path : require('../../../../../assets/images/brand.svg').default}
                    height={38}
                    style={{ marginBottom: 10 }}
                    alt={t('common.logo')}
                  />
                  <div style={{ fontWeight: 800, fontSize: 18, color: "#1d1d1f", lineHeight: 1.2 }}>
                    { companyData?.legal_name || t('common.companyNamePlaceholder') }
                  </div>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                    { companyData?.street_address || t('sales.addressPlaceholder') }<br />
                    { companyData?.city || t('common.city') } { companyData?.state || t('common.state') } { companyData?.postal_code || t('common.postalCode') }<br />
                    { printSlug(companyData?.country) || t('common.country') }<br />
                    { companyData?.email || t('common.emailPlaceholder') }
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 800, fontSize: 26, color: PRIMARY, letterSpacing: 2, textTransform: "uppercase", lineHeight: 1 }}>
                    {t('sales.paymentReceipt')}
                  </div>
                </div>
              </div>

              <hr style={DOC_STYLES.divider} />

              {/* Amount Hero */}
              <div style={{ background: "linear-gradient(135deg, #f4f2ff 0%, #ede9ff 100%)", border: "1px solid #ddd6ff", borderRadius: 12, padding: "28px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: "#9b93e0", textTransform: "uppercase", marginBottom: 6 }}>{t('sales.amountReceived')}</div>
                  <div style={{ fontSize: 42, fontWeight: 800, color: PRIMARY, lineHeight: 1 }}>
                    { payment.amount_received + " " + payment.currency }
                  </div>
                </div>
                <div style={{ background: "#fff", border: "1px solid #ddd6ff", borderRadius: 10, padding: "16px 24px", textAlign: "right", minWidth: 200 }}>
                  <div style={{ marginBottom: 10 }}>
                    <div style={DOC_STYLES.label}>{t('sales.paymentDate')}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                      { formatDate(payment.payment_date) }
                    </div>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={DOC_STYLES.label}>{t('sales.referenceHash')}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                      { payment.reference || t('common.notAvailable') }
                    </div>
                  </div>
                  <div>
                    <div style={DOC_STYLES.label}>{t('sales.paymentMode')}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                      { payment.payment_method || t('common.notAvailable') }
                    </div>
                  </div>
                </div>
              </div>

              {/* Received From */}
              <div style={{ marginBottom: 32 }}>
                <div style={DOC_STYLES.label} >{t('sales.receivedFrom')}</div>
                <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8, padding: "16px 20px", marginTop: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1d1d1f", marginBottom: 4 }}>
                    { payment?.customer?.company_name || payment?.customer?.firstname + ' ' + payment?.customer?.lastname || t('common.notAvailable') }
                  </div>
                  <div style={{ fontSize: 12, color: "#777", lineHeight: 1.8 }}>
                    { payment?.customer?.addresses[0]?.street || "" }<br />
                    { payment?.customer?.addresses[0]?.city || "" }, 
                    { payment?.customer?.addresses[0]?.state || "" } 
                    { payment?.customer?.addresses[0]?.postal_code || "" }<br />
                    { payment?.customer?.addresses[0]?.country || "" }<br />
                    { payment?.customer?.email || "" }
                  </div>
                </div>
              </div>

              {payment?.invoice &&
                <Fragment>
                  <hr style={DOC_STYLES.divider} />

                  {/* Payment For */}
                  <div style={DOC_STYLES.label}>{t('sales.paymentFor')}</div>
                  <table style={{ ...DOC_STYLES.itemsTable, marginTop: 10 }}>
                    <thead>
                      <tr>
                        <th style={DOC_STYLES.th}>{t('sales.invoiceHash')}</th>
                        <th style={DOC_STYLES.th}>{t('sales.invoiceDate')}</th>
                        <th style={DOC_STYLES.th}>{t('sales.dueDate')}</th>
                        <th style={{ ...DOC_STYLES.thRight, borderRadius: "0 6px 0 0" }}>{t('sales.amountApplied')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ background: "#fff" }}>
                        <td style={DOC_STYLES.td}>
                          <span style={{ fontWeight: 600, color: PRIMARY }}>
                            { payment.invoice.invoice_number }
                          </span>
                        </td>
                        <td style={DOC_STYLES.td}>
                          { formatDate(payment.invoice.invoice_date) }
                        </td>
                        <td style={DOC_STYLES.td}>
                          { formatDate(payment.invoice.due_date) }
                        </td>
                        <td style={DOC_STYLES.tdRight}>
                          { payment.amount_received + " " + payment.currency }
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Fragment>
              }

              {/* Footer note */}
              <div style={{ marginTop: 40, paddingTop: 20, textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.8 }}>
                  {t('sales.receiptFooterNote')}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </DefaultDrawer>
  );
}